import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectRemindersError,
  setReminderWorker,
  clearError
} from 'store/slices/remindersSlice';
import {
  fetchWorkers,
  selectAllWorkers,
  selectWorkersLoading
} from 'store/slices/workersSlice';
import { apiClient, buildQueryString } from 'utils/api';
import { Button, PrimaryButton } from 'components/buttons';
import { DebouncedSearchInput, SelectField, ToggleField } from 'components/forms';
import { Skeleton } from 'components/layout/loading';
import { AlertError, AlertWarning } from 'components/layout/alerts';
import { REMINDER_STATUSES } from 'constants/reminders';
import ReminderListItem from '../ReminderListItem/ReminderListItem';

const hasAssignedWorker = (reminder) => Boolean(reminder?.worker);
const isAttachableReminder = (reminder) => (
  reminder?.status === REMINDER_STATUSES.ACTIVE || reminder?.status === REMINDER_STATUSES.PAUSED
);

export default function AttachWorkerToReminder({
  workerId: propWorkerId,
  onSuccess,
  onCancel,
  showWorkerInfo = false,
  cancelLabel = 'Cancel',
  successLabel,
  className = ''
}) {
  const dispatch = useDispatch();
  const reminderError = useSelector(selectRemindersError);

  const allWorkers = useSelector(selectAllWorkers);
  const loadingWorkers = useSelector(selectWorkersLoading);
  const isInitialLoadingWorkers = loadingWorkers && allWorkers.length === 0;
  const user = useSelector((state) => state.auth.user);

  const [reminderResults, setReminderResults] = useState([]);
  const [loadingReminderResults, setLoadingReminderResults] = useState(false);
  const [reminderSearchError, setReminderSearchError] = useState(null);
  const [hasFetchedReminders, setHasFetchedReminders] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState(propWorkerId || null);
  const [searchFilterValue, setSearchFilterValue] = useState('');
  const [showAssignedReminders, setShowAssignedReminders] = useState(true);
  const [isAttaching, setIsAttaching] = useState(false);
  const reminderSearchRequestIdRef = useRef(0);

  useEffect(() => {
    dispatch(clearError());
    if (!propWorkerId) {
      dispatch(fetchWorkers());
    }
  }, [dispatch, propWorkerId]);

  useEffect(() => {
    if (propWorkerId) {
      setSelectedWorkerId(propWorkerId);
    }
  }, [propWorkerId]);

  const handleSelectReminder = (reminder) => {
    const currentId = selectedReminder?._id;
    const targetId = reminder._id;
    setSelectedReminder(currentId === targetId ? null : reminder);
  };

  const fetchReminderResults = useCallback(async (nextSearchFilterValue) => {
    const normalizedSearch = nextSearchFilterValue.trim();
    const requestId = reminderSearchRequestIdRef.current + 1;

    reminderSearchRequestIdRef.current = requestId;

    if (!normalizedSearch) {
      setHasFetchedReminders(false);
      setReminderResults([]);
      setReminderSearchError(null);
      setLoadingReminderResults(false);
      return;
    }

    setHasFetchedReminders(true);
    setLoadingReminderResults(true);
    setReminderSearchError(null);

    try {
      const qs = buildQueryString({
        search: normalizedSearch,
        status: `${REMINDER_STATUSES.ACTIVE},${REMINDER_STATUSES.PAUSED}`,
      });
      const response = await apiClient.get(qs ? `/reminders?${qs}` : '/reminders');

      if (reminderSearchRequestIdRef.current !== requestId) {
        return;
      }

      setReminderResults((response.data?.data || []).filter(isAttachableReminder));
    } catch (error) {
      if (reminderSearchRequestIdRef.current !== requestId) {
        return;
      }

      setReminderResults([]);
      setReminderSearchError(error.response?.data || 'Failed to fetch reminders');
    } finally {
      if (reminderSearchRequestIdRef.current === requestId) {
        setLoadingReminderResults(false);
      }
    }
  }, []);

  const filteredReminders = useMemo(() => (
    reminderResults.filter((reminder) => {
      if (!showAssignedReminders && hasAssignedWorker(reminder)) {
        return false;
      }

      return true;
    })
  ), [reminderResults, showAssignedReminders]);

  useEffect(() => {
    if (!selectedReminder) {
      return;
    }

    const reminderStillVisible = filteredReminders.some((reminder) => reminder._id === selectedReminder._id);

    if (!reminderStillVisible) {
      setSelectedReminder(null);
    }
  }, [filteredReminders, selectedReminder]);

  const handleAttach = async () => {
    if (!selectedReminder || !selectedWorkerId) {
      return;
    }

    try {
      setIsAttaching(true);
      await dispatch(setReminderWorker({
        reminderId: selectedReminder._id,
        workerId: selectedWorkerId,
      })).unwrap();

      if (onSuccess) onSuccess();
    } catch (_error) {
      // error is captured in Redux state via rejectWithValue and displayed in the UI
    } finally {
      setIsAttaching(false);
    }
  };

  const workerOptions = allWorkers.map((worker) => ({ value: worker._id, label: worker.name }));
  const selectedWorker = allWorkers.find((worker) => worker._id === selectedWorkerId);
  const emptyMessage = !hasFetchedReminders
    ? 'Search for reminders to see results.'
    : (!showAssignedReminders && reminderResults.length > 0 && filteredReminders.length === 0)
      ? 'No reminders without workers found.'
      : 'No matching reminders found.';

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {reminderError && (
        <div className="mb-4">
          <AlertError title="Error">{reminderError}</AlertError>
        </div>
      )}

      {reminderSearchError && (
        <div className="mb-4">
          <AlertError title="Error">{reminderSearchError}</AlertError>
        </div>
      )}

      {showWorkerInfo && selectedWorker && (
        <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
          <h3 className="text-sm font-medium text-gray-300">Selected Worker</h3>
          <p className="text-white font-semibold">{selectedWorker.name}</p>
        </div>
      )}

      {!propWorkerId && (
        <div className="mb-4">
          <SelectField
            label="Select Worker"
            options={workerOptions}
            value={selectedWorkerId}
            onChange={setSelectedWorkerId}
            loading={isInitialLoadingWorkers}
            placeholder="Choose a worker..."
          />
        </div>
      )}

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end">
        <DebouncedSearchInput
          id="attach-worker-search"
          label="Search reminders"
          placeholder="Search reminders..."
          value={searchFilterValue}
          onChange={(event) => {
            const nextValue = event.target.value;

            setSearchFilterValue(nextValue);
            fetchReminderResults(nextValue);
          }}
          disabled={isAttaching}
          className="w-full sm:flex-1"
        />
        <ToggleField
          id="show-assigned-reminders"
          label="Show reminders with workers"
          checked={showAssignedReminders}
          onChange={setShowAssignedReminders}
          disabled={isAttaching}
          className="w-fit max-w-full sm:shrink-0"
        />
      </div>

      <div className="space-y-4 flex-1 overflow-y-auto pr-2 custom-scrollbar min-h-[200px] max-h-[400px]">
        {loadingReminderResults && !isAttaching ? (
          <>
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </>
        ) : (
          <>
            {filteredReminders.map(reminder => (
              <ReminderListItem
                key={reminder._id}
                reminder={reminder}
                isSelected={selectedReminder?._id === reminder._id}
                onSelect={() => handleSelectReminder(reminder)}
                settings={user?.settings}
              />
            ))}
            {filteredReminders.length === 0 && (
              <p className="text-center text-gray-400 py-8">
                {emptyMessage}
              </p>
            )}
          </>
        )}
      </div>

      {hasAssignedWorker(selectedReminder) && (
        <div className="mt-4">
          <AlertWarning>
            This reminder already has a worker assigned. Assigning a new worker will replace the existing one.
          </AlertWarning>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-y-2">
        <PrimaryButton
          onClick={handleAttach}
          disabled={!selectedReminder || !selectedWorkerId || loadingReminderResults || isInitialLoadingWorkers || isAttaching}
          className="w-full"
        >
          {isAttaching ?
            (selectedReminder ? 'Attaching...' : 'Processing...') :
            (successLabel || (selectedReminder ? 'Attach & Finish' : 'Finish'))}
        </PrimaryButton>
        {onCancel && (
          <Button
            onClick={onCancel}
            className="w-full"
            disabled={isAttaching}
          >
            {cancelLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
